import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Collapse, 
  Divider,
  Typography
} from '@material-ui/core';
import { 
  Description, 
  Edit, 
  ExpandLess, 
  ExpandMore, 
  Gavel,
  Create
} from '@material-ui/icons';
import { NavLink, useLocation } from 'react-router-dom';

const drawerWidth = 260;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'normal',
    position: 'fixed',
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: '#f8f8f8',
    borderRight: '1px solid #e0e0e0',
    overflowX: 'hidden',
    position: 'fixed',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
  drawerOpen: {
    transform: 'translateX(0)',
  },
  drawerClose: {
    transform: 'translateX(-100%)',
    borderRight: 'none',
  },
  toolbar: theme.mixins.toolbar,
  nested: {
    paddingLeft: theme.spacing(4),
  },
  listItemIcon: {
    minWidth: 40,
  },
  menuText: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  parentMenuItem: {
    backgroundColor: '#eeeeee',
    borderLeft: '4px solid #9e9e9e',
    borderRadius: '4px 0 0 4px',
    margin: '4px 0',
    '& .MuiListItemText-primary': {
      fontWeight: 'bold',
      whiteSpace: 'normal',
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.text.secondary,
    },
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
  submenuItem: {
    paddingLeft: theme.spacing(4),
    borderLeft: '4px solid transparent',
    marginLeft: theme.spacing(1),
    borderBottom: '1px solid #f0f0f0',
    '& .MuiListItemText-primary': {
      whiteSpace: 'normal',
    },
  },
  submenuItemActive: {
    paddingLeft: theme.spacing(4),
    backgroundColor: 'rgba(244, 67, 54, 0.08)',
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    marginLeft: theme.spacing(1),
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
    '& .MuiListItemText-primary': {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
      whiteSpace: 'normal',
    },
  },
  expandIcon: {
    color: theme.palette.text.secondary,
  },
  collapsibleSection: {
    marginBottom: theme.spacing(1),
  },
}));

const SideMenu = ({ open, setOpen }) => {
  const classes = useStyles();
  const location = useLocation();
  
  // Estado para controlar a abertura de submenus
  const [openMenus, setOpenMenus] = useState({
    processamento: true, // Inicialmente aberto
    criadorPecas: false
  });
  
  // Função para alternar a abertura/fechamento de um submenu
  const handleToggleSubmenu = (menu) => {
    setOpenMenus({
      ...openMenus,
      [menu]: !openMenus[menu]
    });
  };
  
  // Função para fechar o drawer após clicar em um item
  const handleMenuItemClick = () => {
    setOpen(false);
  };
  
  // Verificar se um link está ativo
  const isActive = (path) => {
    return location.pathname === path;
  };

  const drawer = (
    <div>
      <div className={classes.toolbar} />
      <Divider />
      
      <List>
        {/* Menu de Processamento de Documentos */}
        <div className={classes.collapsibleSection}>
          <ListItem 
            button 
            onClick={() => handleToggleSubmenu('processamento')}
            className={classes.parentMenuItem}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <Description />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography variant="subtitle2" className={classes.menuText}>
                  Processamento de Documentos
                </Typography>
              } 
            />
            {openMenus.processamento ? 
              <ExpandLess className={classes.expandIcon} /> : 
              <ExpandMore className={classes.expandIcon} />
            }
          </ListItem>
          <Collapse in={openMenus.processamento} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                button
                component={NavLink}
                to="/editor-templates"
                className={isActive('/editor-templates') ? classes.submenuItemActive : classes.submenuItem}
                onClick={handleMenuItemClick}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <Edit />
                </ListItemIcon>
                <ListItemText 
                  primary="Editor de Templates" 
                  className={classes.menuText}
                />
              </ListItem>
            </List>
          </Collapse>
        </div>
        
        <Divider style={{ margin: '8px 0' }} />
        
        {/* Menu de Criador Inteligente de Peças */}
        <div className={classes.collapsibleSection}>
          <ListItem 
            button 
            onClick={() => handleToggleSubmenu('criadorPecas')}
            className={classes.parentMenuItem}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <Gavel />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography variant="subtitle2" className={classes.menuText}>
                  Criador Inteligente de Peças
                </Typography>
              } 
            />
            {openMenus.criadorPecas ? 
              <ExpandLess className={classes.expandIcon} /> : 
              <ExpandMore className={classes.expandIcon} />
            }
          </ListItem>
          <Collapse in={openMenus.criadorPecas} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                button
                component={NavLink}
                to="/contestacao"
                className={isActive('/contestacao') ? classes.submenuItemActive : classes.submenuItem}
                onClick={handleMenuItemClick}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <Create />
                </ListItemIcon>
                <ListItemText 
                  primary="Contestação" 
                  className={classes.menuText}
                />
              </ListItem>
            </List>
          </Collapse>
        </div>
      </List>
    </div>
  );

  return (
    <Drawer
      variant="permanent"
      className={classes.drawer}
      classes={{
        paper: `${classes.drawerPaper} ${open ? classes.drawerOpen : classes.drawerClose}`,
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default SideMenu; 